'use client';

/**
 * SysObeyaSynopsisCard — generic two-register LLM synopsis card.
 *
 * Selection-aware: by default the card reads `explorerSelection`
 * from layout-store and synthesizes THAT selection's view (so
 * sel:module_summary gets the module_summary synopsis;
 * sel:high_cc_functions gets the high_cc_functions synopsis;
 * each future selection synthesizes its own view). The `view_name`
 * prop is an override if a caller wants to pin it.
 *
 * Mounted on the right rail (`insights`) for all mode9 presets;
 * loads in parallel with the canvas data. Calls the
 * `system_obeya_synopsis` BFF strategy. The strategy ships a
 * `{ headline, body, model, generated_at, *_tokens }` payload via
 * Gemini schema-enforced structured output.
 *
 * Caching: 60-min staleTime — synthesis is expensive (LLM call,
 * ~thousands of tokens). The Refresh button manually invalidates
 * for an on-demand fresh synthesis. Server-side `run_id`-keyed
 * cache is the future enhancement (parked in
 * `llm_responses_concept.md`).
 *
 * Language: viewer language passes through the lang precedence
 * chain (`params.lang` → `actor.preferred_language` → 'en'); the
 * synthesizer threads it into the prompt so the prose is localised.
 *
 * @see klyde_edge/docs/llm_responses_concept.md
 * @see klyde_edge/klyde_intelligence/templates/synopsis.py
 */

import { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, Copy, Check } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { api } from '@/lib/api/client';
import { useSystemObeyaView } from './use-system-obeya-view';

interface Props {
  /** Override the auto-derived view_name. Default: read from
   *  `useLayoutStore.explorerSelection` (selection-aware). */
  view_name?: string;
}

interface Synopsis {
  headline: string;
  body: string;
  model?: string;
  generated_at?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  thought_tokens?: number;
}

// 60-minute stale window — synthesis is expensive; we don't want to
// re-fetch on every remount / tab refresh. The Refresh button
// invalidates manually.
const STALE_MS = 60 * 60 * 1000;

export function SysObeyaSynopsisCard({ view_name }: Props) {
  const T = useCardTypography();
  const strategy = useCapabilityStore((s) => s.strategyName('system_obeya', 'synopsize'));
  const lang = useLanguageStore((s) => s.lang);
  const queryClient = useQueryClient();

  // Selection-aware: explorerSelection drives view_name unless the
  // caller pinned one via props. Default to module_summary when
  // nothing's selected (the landing experience).
  const explorerSelection = useLayoutStore((s) => s.explorerSelection);
  const effectiveViewName = view_name ?? explorerSelection ?? 'module_summary';

  // Chrome labels piggyback on the view's labels dict — same lang
  // precedence, deduped by React Query with the canvas cards so this
  // is free when the canvas is also mounted (and pulls fresh chrome
  // when only the rail is rendered, e.g. mobile). Layer 1 / Pattern A
  // — see i18n_concept.md line 87.
  const { data: viewData } = useSystemObeyaView(effectiveViewName);
  const L = viewData?.labels ?? {};

  const queryKey = ['system-obeya-synopsis', strategy, effectiveViewName, lang];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.getStrategyData<Synopsis>(
        strategy ?? 'system_obeya_synopsis',
        { view_name: effectiveViewName, ...(lang ? { lang } : {}) },
      );
      if (!res.success) throw new Error(res.error || `${strategy} failed`);
      return res.data;
    },
    enabled: !!effectiveViewName,
    staleTime: STALE_MS,
  });

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey });
  };
  const isRefreshing = isFetching && !isLoading;

  // Card export — registered via the doctrine-standard primitive.
  // The local Copy button reads from the registry; container
  // surfaces (preset-level "Copy report" + scene snapshot) pick
  // this up too. Surface = 'rail' (mode9 mounts the synopsis on
  // the right rail via @preset.insights).
  //
  // `status` disambiguates `data: null` — `loading` vs `error` vs
  // `loaded` (genuinely no data) are three different things wearing
  // one value otherwise. `state.lang` resolves through the doctrine
  // chain (terminates at 'en'); never null.
  const synopsisTitle = L.synopsis_title ?? '';
  // Status derives from PAINTABLE CONTENT, not React Query's
  // `isLoading` flag. The scene's status field answers "what does
  // the user see?", not "is a fetch in flight?" — these aren't the
  // same during refetches, background revalidation, or any tick
  // where the body has rendered but the flag hasn't flipped.
  // `data` present = there's a body to paint = loaded.
  const cardStatus: 'loading' | 'loaded' | 'error' = error
    ? 'error'
    : data
      ? 'loaded'
      : 'loading';
  useCardExport({
    label: synopsisTitle,
    text: data ? `${data.headline}\n\n${data.body}` : '',
    markdown: data ? `**${data.headline}**\n\n${data.body}` : '',
    json: data
      ? {
          headline: data.headline,
          body: data.body,
          model: data.model,
          generated_at: data.generated_at,
          tokens: data.total_tokens,
        }
      : null,
    content_key: 'sys_obeya_synopsis',
    surface: 'rail',
    state: { view_name: effectiveViewName, lang: lang ?? 'en' },
    status: cardStatus,
    error: error ? (error as Error).message : undefined,
    // Capture-envelope derivation source — the same React Query key
    // this card uses for `useQuery` above. Mounted cards' query_keys
    // are deduped + intersected with the cache to scope the envelope
    // (see card-export.tsx). Zero new knowledge: this key is
    // already in scope here for the hook itself.
    query_key: queryKey,
  });

  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!data) return;
    const text = `${data.headline}\n\n${data.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[SynopsisCard] copy failed:', err);
    }
  };

  return (
    <CardShell>
      <div className="p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className={T.header}>{L.synopsis_title ?? ''}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!data || isLoading}
              aria-label={L.synopsis_copy_aria ?? ''}
              className={cn(
                'p-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed',
                copied ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isLoading || isFetching}
              aria-label={L.synopsis_refresh_aria ?? ''}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
            </button>
          </div>
        </div>
        {isLoading && (
          <div className={cn(T.body, 'flex items-center gap-2 text-muted-foreground')}>
            <Loader2 className="w-4 h-4 animate-spin" />
            {L.synopsis_loading ?? ''}
          </div>
        )}
        {error && !isLoading && (
          <div className={cn(T.body, 'text-destructive')}>
            {L.synopsis_failed_prefix ?? ''}{(error as Error).message}
          </div>
        )}
        {data && !error && (
          <div className="space-y-4 overflow-auto flex-1">
            <p className={cn(T.body, 'font-medium text-foreground leading-snug')}>
              {data.headline}
            </p>
            <div className={cn(T.body, 'text-muted-foreground leading-relaxed whitespace-pre-wrap')}>
              {data.body}
            </div>
            {(data.model || data.total_tokens) && (
              <div className={cn(T.metadata, 'pt-2 border-t border-border/40')}>
                {[
                  data.model || null,
                  data.generated_at ? data.generated_at.slice(0, 19).replace('T', ' ') : null,
                  data.total_tokens ? `${data.total_tokens.toLocaleString()} ${L.synopsis_tokens_unit ?? ''}`.trim() : null,
                ].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        )}
      </div>
    </CardShell>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
